import {
  Avatar,
  Box,
  Divider,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  Tooltip,
  useTheme,
} from "@mui/material";
import { usePostHog } from "@posthog/react";
import { getFullName, logOut } from "components/navigation/menu-bar";
import { NameEmailAvatarRound, stringAvatar } from "components/shared/shared";
import {
  switchedOrganisationCookieName,
  useIdentity,
  User,
} from "contexts/identity-context";
import {
  ChevronsUpDown,
  Inbox,
  LogOut,
  Monitor,
  Moon,
  Shield,
  SquareUser,
  Sun,
} from "lucide-react";
import { useState } from "react";
import { useCookies } from "react-cookie";
import { useIntercom } from "react-use-intercom";
import useDarkMode from "use-dark-mode";
import * as Routes from "../../routes";
import { MenuItem } from "./side-bar";

interface ProfileMenuProps {
  drawerOpen: boolean;
}

const profileItems: MenuItem[] = [
  {
    name: "Pending Invitations",
    link: Routes.userPendingInvitations,
    IconLeft: Inbox,
  },
  {
    name: "User Profile",
    link: Routes.userGeneralSettings,
    IconLeft: SquareUser,
  },
  {
    name: "Security Settings",
    link: Routes.userSecurity,
    IconLeft: Shield,
  },
];
export const ProfileMenu = (props: ProfileMenuProps) => {
  const [identityState, setIdentityState] = useIdentity();
  const darkMode = useDarkMode(false);

  const [_c, setCookie, removeCookie] = useCookies([
    switchedOrganisationCookieName,
  ]);
  const { update, shutdown } = useIntercom();
  const posthog = usePostHog();

  const identityName = getFullName(identityState);
  const isLoading = identityState === "loading";
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [anchorElTheme, setAnchorElTheme] = useState<HTMLDivElement | null>(
    null
  );
  const theme = useTheme();

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClickTheme = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorElTheme(event.currentTarget);
  };

  const handleCloseTheme = () => {
    setAnchorElTheme(null);
  };

  const open = Boolean(anchorEl);
  const openTheme = Boolean(anchorElTheme);

  const handleSystemTheme = () => {
    const isDark = window.matchMedia("(prefers-color-scheme: dark)");
    if (isDark.matches) {
      darkMode.enable();
    } else {
      darkMode.disable();
    }
  };

  return (
    <>
      {!props.drawerOpen && (
        <Box
          pt="2px"
          pb="2px"
          pl={2}
          pr={2}
          onClick={handleClick}
          sx={{ cursor: "pointer" }}
        >
          <Avatar {...stringAvatar(identityName)} variant="rounded" />
        </Box>
      )}
      {props.drawerOpen && (
        <>
          <ListItemButton
            onClick={handleClick}
            sx={{
              paddingLeft: 2,
              paddingBottom: 0,
              paddingTop: 0,
            }}
          >
            <NameEmailAvatarRound
              variant="rounded"
              name={identityName}
              email={(identityState as User).email}
              isLoading={identityState === "loading"}
              textBoxWidth="128px"
            />
            <Box sx={{ width: "24px", height: "24px" }}>
              <ChevronsUpDown />
            </Box>
          </ListItemButton>
        </>
      )}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        elevation={3}
        slotProps={{
          paper: {
            style: {
              backgroundImage: "none",
              boxShadow:
                "0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)",
            },
          },
        }}
      >
        <List sx={{ width: "230px", pt: 0 }}>
          <ListItem disablePadding>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                p: 1,
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <NameEmailAvatarRound
                variant="rounded"
                name={identityName}
                email={(identityState as User).email}
                isLoading={identityState === "loading"}
                textBoxWidth="158px"
              />
            </Stack>
          </ListItem>
          {open && (
            <>
              <Divider />
              {profileItems.map((item) => {
                return (
                  <ListItemButton
                    sx={{
                      justifyContent: "flex-start",
                      padding: "4px 8px 4px 8px",
                      height: "32px",
                      width: "230px",
                      justifyItems: "center",
                      borderRadius: 1,
                      gap: 1,
                    }}
                    component={item.target ? ListItemButton : Link}
                    href={item.subMenus ? undefined : item.link}
                    onClick={handleClose}
                  >
                    {item.IconLeft && !item.subMenus && (
                      <Tooltip
                        title={
                          open ? (
                            ""
                          ) : (
                            <ListItemText
                              primaryTypographyProps={{
                                variant: "body2",
                              }}
                              primary={item.name}
                            />
                          )
                        }
                        arrow
                        placement="right"
                      >
                        <item.IconLeft size={24} />
                      </Tooltip>
                    )}
                    <ListItemText primary={item.name} />
                    {item.IconRight && (
                      <item.IconRight
                        size={24}
                        color={theme.palette.secondary.main}
                      />
                    )}
                  </ListItemButton>
                );
              })}
              <ListItemButton
                sx={{
                  justifyContent: "flex-start",
                  padding: "4px 8px 4px 8px",
                  height: "32px",
                  width: "230px",
                  justifyItems: "center",
                  borderRadius: 1,
                  gap: 1,
                }}
                onClick={handleClickTheme}
              >
                <Sun size={24} />
                <ListItemText primary="Theme" />
              </ListItemButton>
              <ListItemButton
                sx={{
                  justifyContent: "flex-start",
                  padding: "4px 8px 4px 8px",
                  height: "40px",
                  width: "230px",
                  justifyItems: "center",
                  borderRadius: 1,
                  gap: 1,
                }}
                onClick={() =>
                  logOut(setIdentityState, removeCookie, shutdown, posthog)
                }
              >
                <LogOut size={24} />
                <ListItemText primary="Sign Out" />
              </ListItemButton>
            </>
          )}
        </List>
      </Popover>
      <Popover
        open={openTheme}
        anchorEl={anchorElTheme}
        onClose={handleCloseTheme}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        elevation={3}
        slotProps={{
          paper: {
            style: {
              backgroundImage: "none",
              boxShadow:
                "0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)",
            },
          },
        }}
      >
        <List
          sx={{
            width: "128px",
          }}
        >
          <ListItemButton
            sx={{
              justifyContent: "flex-start",
              padding: "4px 8px 4px 8px",
              height: "32px",
              justifyItems: "center",
              borderRadius: 1,
              gap: 1,
            }}
            onClick={darkMode.disable}
          >
            <Sun size={24} />
            <ListItemText primary="Light" />
          </ListItemButton>
          <ListItemButton
            sx={{
              justifyContent: "flex-start",
              padding: "4px 8px 4px 8px",
              height: "32px",
              justifyItems: "center",
              borderRadius: 1,
              gap: 1,
            }}
            onClick={darkMode.enable}
          >
            <Moon size={24} />
            <ListItemText primary="Dark" />
          </ListItemButton>
          <ListItemButton
            sx={{
              justifyContent: "flex-start",
              padding: "4px 8px 4px 8px",
              height: "32px",
              justifyItems: "center",
              borderRadius: 1,
              gap: 1,
            }}
            onClick={handleSystemTheme}
          >
            <Monitor size={24} />
            <ListItemText primary="System" />
          </ListItemButton>
        </List>
      </Popover>
    </>
  );
};
